import React, { useRef } from 'react';
import { InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { CalendarIcon, DateRangePicker } from '@mui/x-date-pickers-pro';
import { Controller } from 'react-hook-form';
import { validateDateRequired, isDiscountActive } from 'utils';
import { useCampaignForm } from 'hooks';
import { CampaignFields } from 'types';
import { DateTime } from 'luxon';
import {
  MIN_DAYS_BEFORE_CAMPAIGN_START,
  numberInputStyling,
  DISCOUNT_SUCCESS_MESSAGE_TITLE,
  DISCOUNT_SUCCESS_MESSAGE_DESCRIPTION,
} from 'shared';
import { WebsiteList } from './WebsiteList';
import { InfoMessage } from 'components/InfoMessage';

export const Basic = () => {
  const periodRef = useRef<HTMLDivElement>(null);
  const {
    formState: { errors },
    control,
    getFieldControls,
    onFieldChange,
    setHasInThePastError,
    isCreateCampaignFlow,
  } = useCampaignForm();

  return (
    <Stack gap={10}>
      <Stack gap={6}>
        <Typography variant="subtitle1">Name der Kampagne</Typography>
        <Stack width={{ xs: 1, lg: 2 / 3 }}>
          <TextField
            autoFocus={isCreateCampaignFlow}
            label="Wie möchtest du die Kampagne benennen?"
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
            variant="outlined"
            size="small"
            helperText={errors.name?.message}
            error={!!errors.name?.message}
            {...getFieldControls(CampaignFields.name, { required: 'Fülle bitte das Feld aus' })}
          />
        </Stack>
      </Stack>

      <Stack alignItems="start">
        <Typography variant="subtitle1" mb={6}>
          Auf diese Webseite gelangen Nutzer, nachdem sie auf deine Anzeige geklickt haben
        </Typography>
        <WebsiteList />
      </Stack>

      <Stack gap={6}>
        <Typography variant="subtitle1">Dauer der Kampagne</Typography>
        <Stack width={{ xs: 1, lg: 2 / 3 }}>
          <Controller
            control={control}
            name={CampaignFields.campaignPeriod}
            rules={{
              validate: validateDateRequired,
            }}
            render={({ field, fieldState }) => (
              <DateRangePicker
                localeText={{ start: 'Start der Kampagne', end: 'Ende der Kampagne' }}
                disablePast
                displayWeekNumber
                minDate={DateTime.local().plus({ days: MIN_DAYS_BEFORE_CAMPAIGN_START })}
                value={field.value}
                ref={periodRef}
                onChange={(period) => {
                  setHasInThePastError(false);
                  onFieldChange(CampaignFields.campaignPeriod);
                  return field.onChange(period);
                }}
                shouldDisableDate={(day, position) =>
                  !!field.value[0] && position === 'end' && day.hasSame(field.value[0], 'day')
                }
                slotProps={{
                  popper: {
                    anchorEl: periodRef.current,
                  },
                  textField: ({ position }) => ({
                    error: !!fieldState.error,
                    helperText:
                      fieldState.error?.message ||
                      (position === 'start' &&
                        'Das Startdatum muss mindestens 5 Tage ab heute liegen. Alloy benötigt 5 Tage, um die Kampagne zu prüfen und einzurichten.'),
                    placeholder: 'TT.MM.JJJJ',
                    slotProps: {
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon sx={{ width: '14px' }} />
                          </InputAdornment>
                        ),
                        size: 'small',
                        sx: { input: { paddingLeft: 0 } },
                      },
                    },
                  }),
                }}
              />
            )}
          />
        </Stack>
      </Stack>

      <Stack gap={6}>
        <Typography variant="subtitle1">Gesamtbudget</Typography>
        <Stack width={{ lg: 1 / 3.15 }}>
          <TextField
            fullWidth
            label="Budget in CHF"
            variant="outlined"
            size="small"
            type="number"
            sx={numberInputStyling}
            slotProps={{
              input: { startAdornment: <InputAdornment position="start">CHF</InputAdornment> },
            }}
            helperText={
              errors.budget?.message ||
              'Das ist dein Gesamtbudget exkl. MwSt. Das Budget wird automatisch auf die Kampagnenzeit verteilt. Somit wird sichergestellt, dass das Budget für die gesamte Kampagnendauer ausreicht.'
            }
            error={!!errors.budget?.message}
            {...getFieldControls(CampaignFields.budget, {
              min: { value: 1, message: 'Überprüfe bitte deine Angabe und gib ein geeignetes Budget ein' },
              required: 'Fülle bitte das Feld aus',
            })}
          />
        </Stack>
        {isDiscountActive() && (
          <InfoMessage
            variant="success"
            title={DISCOUNT_SUCCESS_MESSAGE_TITLE}
            message={<Typography variant="body2">{DISCOUNT_SUCCESS_MESSAGE_DESCRIPTION}</Typography>}
          />
        )}
      </Stack>
    </Stack>
  );
};
